import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import type {
  AssignmentDetail,
  AssignmentShift,
  BasicFacility,
  ConnectedFacilityPublic,
} from '../../../../../js/api';
import { ApiError, OpenAPI } from '../../../../../js/api';
import { api, request as __request } from '../../../../../js/api/core/request';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { StatusCategoryKinds, StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import { formatDuration, formatDateTimeRange } from '../../../utils/date';
import { SHIFT_TYPE_ICONS, SHIFT_TYPE_ICON_COLORS } from '../../../utils/utils';
import { canUseWorkflowItem, useAudience } from '../../../utils/workflowAccess';
import {
  ASSIGNMENT_QUERY_KEY,
  ASSIGNMENT_SHIFT_CHANGE_HISTORY_QUERY_KEY,
  ASSIGNMENT_SHIFT_STATUS_HISTORY_QUERY_KEY,
  ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
  STAFF_ASSIGNMENTS_LIST_QUERY_KEY,
} from './queryKeys';
import { ShiftEditModal } from './ShiftEditModal';
import { ShiftHistoryModal } from './ShiftHistoryModal';

// --- Types ---

export type ShiftEditValues = {
  shift_type: string;
  shift_rate_type: string;
  start_time: string;
  end_time: string;
  end_on: string;
  facility_id: string;
  facility_name: string;
  bonus_amount: number;
  holiday_amount: number;
};

export type NewShiftValues = ShiftEditValues & { start_on: string };

type PendingChanges = {
  edits: Record<string, ShiftEditValues>;
  cancels: Set<string>;
  additions: NewShiftValues[];
};

const TERMINAL_STATUSES = new Set(['DECLINED', 'WITHDRAWN', 'CANCELED']);

const EMPTY_PENDING: PendingChanges = {
  edits: {},
  cancels: new Set(),
  additions: [],
};

function calcDuration(startOn: string, startTime: string, endOn: string, endTime: string): string {
  const start = new Date(`${startOn}T${startTime}`);
  const end = new Date(`${endOn}T${endTime}`);
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return '--';
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function formatMoney(value: number | null | undefined): string {
  if (value == null || value === 0) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// --- Component ---

type FacilityOption = {
  value: string;
  label: string;
  facility: BasicFacility;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const ShiftsTab: React.FC<{
  assignment: AssignmentDetail;
  primaryFacilityName?: string;
  canEdit?: boolean;
  onSuccess?: () => void;
  onDirtyChange?: (hasDirty: boolean) => void;
  connectedFacility?: ConnectedFacilityPublic | null;
  facilityOptions?: FacilityOption[];
  providerToken?: string;
}> = ({
  assignment,
  primaryFacilityName,
  canEdit = false,
  onSuccess,
  onDirtyChange,
  connectedFacility = null,
  facilityOptions = [],
  providerToken,
}) => {
  const queryClient = useQueryClient();
  const shifts = assignment.shifts || [];
  const isFinalStatus = assignment.current_status?.category_kind === StatusCategoryKinds.FINAL;
  const { data: shiftTypeLabels = {} } = useCodeDefinitionsAsMap('ShiftRateTypes');
  const { statuses: shiftStatuses } = useWorkflowMetadata(WorkflowScopes.AssignmentShift);
  const audience = useAudience();

  // Pending changes state
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>(EMPTY_PENDING);
  const [comment, setComment] = useState('');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<AssignmentShift | null>(null);
  const [editModalMode, setEditModalMode] = useState<'edit' | 'add'>('edit');
  const [editingAdditionIndex, setEditingAdditionIndex] = useState<number | null>(null);
  const [historyShift, setHistoryShift] = useState<AssignmentShift | null>(null);

  const editCount = Object.keys(pendingChanges.edits).length;
  const cancelCount = pendingChanges.cancels.size;
  const addCount = pendingChanges.additions.length;
  const hasPendingChanges = editCount > 0 || cancelCount > 0 || addCount > 0;

  useEffect(() => {
    onDirtyChange?.(hasPendingChanges);
  }, [editCount, cancelCount, addCount, onDirtyChange]);

  // Workflow helpers
  const canCancelShift = (shift: AssignmentShift) => {
    const currentCode = shift.current_status?.code;
    if (!currentCode || !shiftStatuses?.[currentCode]) return false;
    const status = shiftStatuses[currentCode];
    const transitions: string[] = status.allowed_transitions || [];
    const cancelStatus = shiftStatuses['CANCELED'];
    return (
      transitions.includes('CANCELED') &&
      !!cancelStatus &&
      canUseWorkflowItem(audience as any, cancelStatus)
    );
  };

  const isTerminal = (shift: AssignmentShift) =>
    TERMINAL_STATUSES.has(shift.current_status?.code || '');

  // Handlers
  const toggleCancel = (shiftId: string) => {
    setPendingChanges((prev) => {
      const next = { ...prev, cancels: new Set(prev.cancels) };
      if (next.cancels.has(shiftId)) {
        next.cancels.delete(shiftId);
      } else {
        next.cancels.add(shiftId);
      }
      return next;
    });
  };

  const unstageEdit = (shiftId: string) => {
    setPendingChanges((prev) => {
      const { [shiftId]: _, ...rest } = prev.edits;
      return { ...prev, edits: rest };
    });
  };

  const stageEdit = (shiftId: string, values: ShiftEditValues) => {
    setPendingChanges((prev) => ({
      ...prev,
      edits: { ...prev.edits, [shiftId]: values },
    }));
  };

  const stageAddition = (values: NewShiftValues) => {
    setPendingChanges((prev) => ({
      ...prev,
      additions: [...prev.additions, values],
    }));
  };

  const updateAddition = (index: number, values: NewShiftValues) => {
    setPendingChanges((prev) => ({
      ...prev,
      additions: prev.additions.map((a, i) => (i === index ? values : a)),
    }));
  };

  const removeAddition = (index: number) => {
    setPendingChanges((prev) => ({
      ...prev,
      additions: prev.additions.filter((_, i) => i !== index),
    }));
  };

  const discardAll = () => {
    setPendingChanges(EMPTY_PENDING);
    setComment('');
  };

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (payload: {
      edits: {
        id: string;
        shift_rate_type: string;
        start_time: string;
        end_time: string;
        end_on: string;
        facility: string;
        bonus_amount: number;
        holiday_amount: number;
      }[];
      cancels: { id: string }[];
      additions: {
        start_on: string;
        shift_rate_type: string;
        start_time: string;
        end_time: string;
        end_on: string;
        facility: string;
        bonus_amount: number;
        holiday_amount: number;
      }[];
      comment: string;
    }) =>
      __request(OpenAPI, {
        method: 'PATCH',
        url: '/api/assignments/assignment/{id}/shift-changes/',
        path: { id: assignment.id },
        body: payload,
        mediaType: 'application/json',
      }) as Promise<AssignmentDetail>,
    onSuccess: () => {
      toast.success('Shift changes submitted');
      discardAll();
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, assignment.id] });
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_SHIFT_STATUS_HISTORY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_SHIFT_CHANGE_HISTORY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_ASSIGNMENTS_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY] });
      onSuccess?.();
    },
    onError: (error) => handleApiError(error, { handle403: true }),
  });

  const handleSubmit = () => {
    const payload = {
      edits: Object.entries(pendingChanges.edits).map(([id, v]) => ({
        id,
        shift_type: v.shift_type,
        shift_rate_type: v.shift_rate_type,
        start_time: v.start_time,
        end_time: v.end_time,
        end_on: v.end_on,
        facility: v.facility_id,
        bonus_amount: v.bonus_amount,
        holiday_amount: v.holiday_amount,
      })),
      cancels: Array.from(pendingChanges.cancels).map((id) => ({ id })),
      additions: pendingChanges.additions.map((v) => ({
        start_on: v.start_on,
        shift_type: v.shift_type,
        shift_rate_type: v.shift_rate_type,
        start_time: v.start_time,
        end_time: v.end_time,
        end_on: v.end_on,
        facility: v.facility_id,
        bonus_amount: v.bonus_amount,
        holiday_amount: v.holiday_amount,
      })),
      comment,
    };
    submitMutation.mutate(payload);
  };

  // Sorted shifts
  const sortedShifts = useMemo(
    () =>
      [...shifts].sort((a, b) => new Date(a.start_on).getTime() - new Date(b.start_on).getTime()),
    [shifts]
  );

  const uniqueFacilities = new Set(shifts.map((s) => s.facility?.id).filter(Boolean));
  const hasEditedFacility = Object.entries(pendingChanges.edits).some(([id, edit]) => {
    const shift = shifts.find((s) => s.id === id);
    return shift && edit.facility_id !== (shift.facility?.id || '');
  });
  const showFacility =
    uniqueFacilities.size > 1 || pendingChanges.additions.length > 0 || hasEditedFacility;
  const showBonus =
    shifts.some((s) => s.bonus_amount && s.bonus_amount > 0) ||
    pendingChanges.additions.some((a) => a.bonus_amount > 0) ||
    Object.values(pendingChanges.edits).some((e) => e.bonus_amount > 0);
  const showHoliday =
    shifts.some((s) => s.holiday_amount && s.holiday_amount > 0) ||
    pendingChanges.additions.some((a) => a.holiday_amount > 0) ||
    Object.values(pendingChanges.edits).some((e) => e.holiday_amount > 0);

  const isConfirmed = assignment.current_status.code === StatusCodes.CONFIRMED;
  const isClosed = [StatusCodes.CANCELED, StatusCodes.WITHDRAWN].includes(
    assignment.current_status.code as StatusCodes
  );
  const hasCanceledShifts = shifts.some((s) =>
    [StatusCodes.CANCELED, StatusCodes.WITHDRAWN].includes(s.current_status.code as StatusCodes)
  );

  const downloadCalendar = async (url: string) => {
    try {
      await api.get(url, { responseType: 'blob' });
      window.location.assign(url);
    } catch (error: any) {
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        const reader = new FileReader();
        reader.onload = () => {
          const body = JSON.parse(reader.result as string);
          handleApiError(
            new ApiError(
              error.config,
              {
                url: error.config.url,
                status: error.response.status,
                statusText: error.response.statusText,
                body,
                ok: false,
              },
              body.detail || 'Unknown error'
            ),
            { handle403: true }
          );
        };
        reader.readAsText(error.response.data);
      } else {
        handleApiError(error, { handle403: true });
      }
    }
  };

  const handleDownloadCalendar = () =>
    downloadCalendar(`/api/assignments/calendar-ics/${assignment.id}/`);

  const handleDownloadCancellationCalendar = () =>
    downloadCalendar(`/api/assignments/calendar-ics-cancellation/${assignment.id}/`);

  const isInLockoutWindow = useMemo(() => {
    if (!assignment?.start_on) return false;
    if (assignment.current_status?.code !== StatusCodes.CONFIRMED) return false;
    const startDate = parseISO(assignment.start_on);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return differenceInCalendarDays(startDate, today) <= 30;
  }, [assignment?.start_on]);

  // Change summary
  const summaryParts: React.ReactNode[] = [
    editCount > 0 ? (
      <span key="edits" className="tw:text-blue-700">
        {editCount} edit{editCount !== 1 ? 's' : ''}
      </span>
    ) : null,
    cancelCount > 0 ? (
      <span key="cancels" className="tw:text-stoplight-danger">
        {cancelCount} cancel{cancelCount !== 1 ? 's' : ''}
      </span>
    ) : null,
    addCount > 0 ? (
      <span key="adds" className="tw:text-stoplight-success">
        {addCount} new shift{addCount !== 1 ? 's' : ''}
      </span>
    ) : null,
  ].filter(Boolean);

  /* eslint-disable sonarjs/cognitive-complexity */
  if (!shifts.length && !pendingChanges.additions.length) {
    return (
      <div>
        <div className="tw:text-center tw:text-zinc-500 tw:py-8">No shifts scheduled</div>
        {canEdit && !isFinalStatus && (
          <div className="tw:flex tw:justify-end tw:mt-2">
            <Button
              small
              color="brandgreen0"
              onClick={() => {
                setEditingShift(null);
                setEditModalMode('add');
                setEditModalOpen(true);
              }}
            >
              <i className="fas fa-plus tw:mr-1" />
              Add Shift
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* 30-day lockout alert */}
      {isInLockoutWindow && (
        <div className="tw:mb-4 tw:p-4 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-sm tw:text-stoplight-danger">
          <div className="tw:flex tw:items-start tw:gap-2">
            <i className="fas fa-exclamation-triangle tw:text-stoplight-danger tw:mt-0.5" />
            <div>
              <p className="tw:font-medium tw:!mb-1">
                This assignment is within the contracted 30 day window.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Final-status lock hint */}
      {isFinalStatus && (
        <div className="tw:mb-4 tw:p-3 tw:bg-zinc-50 tw:border tw:border-zinc-200 tw:rounded-lg tw:text-sm tw:text-zinc-600">
          <div className="tw:flex tw:items-start tw:gap-2">
            <i className="fas fa-lock tw:text-zinc-400 tw:mt-0.5" />
            <p className="tw:!mb-0">
              This assignment is finalized — its shifts represent the agreed-upon plan and can no
              longer be edited. Record actuals on the timecard.
            </p>
          </div>
        </div>
      )}
      {/* Action buttons */}
      <div className="tw:flex tw:justify-end tw:gap-2 tw:mb-2">
        {assignment.current_status?.code === StatusCodes.PEND_CONFIRM && (
          <Button
            small
            color="brand-green-outline"
            onClick={() =>
              window.open(
                `/staff-portal/assignments/confirmation-letter/${assignment.id}/`,
                '_blank'
              )
            }
          >
            <i className="fas fa-file-lines tw:mr-1" />
            View ACL
          </Button>
        )}
        {isConfirmed && (
          <Button small color="brand-green-outline" onClick={handleDownloadCalendar}>
            <i className="far fa-cloud-download tw:mr-1" />
            Add to Calendar
          </Button>
        )}
        {(isConfirmed || isClosed) && hasCanceledShifts && (
          <Button
            small
            color="stoplight-danger-outline"
            onClick={handleDownloadCancellationCalendar}
          >
            <i className="far fa-calendar-times tw:mr-1" />
            Sync Cancellations
          </Button>
        )}
        {canEdit && !isFinalStatus && (
          <Button
            small
            color="brand-green-outline"
            onClick={() => {
              setEditingShift(null);
              setEditModalMode('add');
              setEditModalOpen(true);
            }}
          >
            <i className="fas fa-plus tw:mr-1" />
            Add Shift
          </Button>
        )}
      </div>

      <Table dense striped>
        <TableHead>
          <TableHeader>
            <span />
          </TableHeader>
          <TableHeader>Date / Time</TableHeader>
          <TableHeader>Duration</TableHeader>
          <TableHeader className="tw:!text-center">Rate Type</TableHeader>
          {showBonus && <TableHeader className="tw:!text-right">Bonus</TableHeader>}
          {showHoliday && <TableHeader className="tw:!text-right">Holiday</TableHeader>}
          <TableHeader className="tw:!text-center">Status</TableHeader>
        </TableHead>
        <TableBody>
          {sortedShifts.map((shift) => {
            const isCanceled = pendingChanges.cancels.has(shift.id);
            const editValues = pendingChanges.edits[shift.id];
            const terminal = isTerminal(shift);
            // Two locks gate edit/cancel: the assignment-level final-kind
            // status (frozen "agreed plan") and the per-shift terminal status
            // (this shift in particular has been canceled/completed/etc.).
            // Either one locks the row's actions.
            const canEditShift = canEdit && !isFinalStatus && !terminal;
            const canCancel = canEditShift && canCancelShift(shift);

            const rowClass = clsx(
              terminal && 'tw:line-through tw:text-zinc-400',
              isCanceled && 'tw:line-through tw:text-stoplight-danger',
              editValues && !isCanceled && !terminal && 'tw:border-l-4 tw:border-l-amber-400'
            );

            return (
              <TableRow key={shift.id} className={rowClass}>
                {/* Actions column */}
                <TableCell>
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <button
                      type="button"
                      className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                      onClick={() => setHistoryShift(shift)}
                      title="View shift history"
                    >
                      <i className="fas fa-clock-rotate-left tw:text-sm" />
                    </button>
                    {canEdit && canEditShift && !isCanceled ? (
                      <button
                        type="button"
                        className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                        onClick={() => {
                          setEditingShift(shift);
                          setEditModalMode('edit');
                          setEditModalOpen(true);
                        }}
                        title="Edit shift"
                      >
                        <i className="fas fa-pencil tw:text-sm" />
                      </button>
                    ) : null}
                    {canEdit && editValues && !isCanceled ? (
                      <button
                        type="button"
                        className="tw:text-stoplight-warning tw:hover:text-stoplight-warning/70 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                        onClick={() => unstageEdit(shift.id)}
                        title="Revert changes"
                      >
                        <i className="fas fa-undo tw:text-sm" />
                      </button>
                    ) : null}
                    {canEdit && canCancel ? (
                      <button
                        type="button"
                        className={`tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0 ${
                          isCanceled
                            ? 'tw:text-stoplight-danger tw:hover:text-stoplight-danger/70'
                            : 'tw:text-zinc-400 tw:hover:text-stoplight-danger'
                        }`}
                        onClick={() => toggleCancel(shift.id)}
                        title={isCanceled ? 'Undo cancel' : 'Cancel shift'}
                      >
                        <i className={`fas tw:text-sm ${isCanceled ? 'fa-undo' : 'fa-xmark'}`} />
                      </button>
                    ) : null}
                  </div>
                </TableCell>
                {/* Date / Time */}
                <TableCell className="tw:whitespace-nowrap">
                  <div>
                    {editValues && !isCanceled ? (
                      <>
                        <div className="tw:text-xs tw:text-zinc-400 tw:line-through">
                          {formatDateTimeRange(shift)}
                        </div>
                        <div>
                          {formatDateTimeRange({
                            start_on: shift.start_on,
                            start_time: editValues.start_time,
                            end_on: editValues.end_on,
                            end_time: editValues.end_time,
                          })}
                        </div>
                      </>
                    ) : (
                      formatDateTimeRange(shift)
                    )}
                  </div>
                  {showFacility && (
                    <div className="tw:text-[12px] tw:leading-tight tw:text-zinc-500">
                      {editValues &&
                      editValues.facility_name !== shift.facility?.name &&
                      !isCanceled ? (
                        <>
                          <div className="tw:text-xs tw:text-zinc-400 tw:line-through">
                            {shift.facility?.name}
                          </div>
                          <div>{editValues.facility_name}</div>
                        </>
                      ) : (
                        <>
                          {primaryFacilityName && shift.facility?.name !== primaryFacilityName && (
                            <i className="far fa-info-circle tw:mr-1 tw:text-blue-600" />
                          )}
                          {shift.facility?.name}
                        </>
                      )}
                    </div>
                  )}
                </TableCell>
                {/* Duration */}
                <TableCell>
                  {editValues && !isCanceled
                    ? (() => {
                        const newDuration = calcDuration(
                          shift.start_on,
                          editValues.start_time,
                          editValues.end_on,
                          editValues.end_time
                        );
                        const origDuration = formatDuration(shift.duration);
                        return newDuration !== origDuration ? (
                          <>
                            <div className="tw:text-xs tw:text-zinc-400 tw:line-through">
                              {origDuration}
                            </div>
                            <div>{newDuration}</div>
                          </>
                        ) : (
                          origDuration
                        );
                      })()
                    : formatDuration(shift.duration)}
                </TableCell>
                {/* Rate Type */}
                <TableCell>
                  {editValues &&
                  editValues.shift_rate_type !== shift.shift_rate_type &&
                  !isCanceled ? (
                    <div className="tw:flex tw:flex-col tw:items-center tw:leading-tight">
                      <div className="tw:flex tw:flex-col tw:items-center tw:text-zinc-400 tw:line-through">
                        <i
                          className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} tw:text-zinc-300`}
                        />
                        <span className="tw:text-[10px]">
                          {shiftTypeLabels[shift.shift_rate_type] || shift.shift_rate_type}
                        </span>
                      </div>
                      <div className="tw:flex tw:flex-col tw:items-center tw:mt-0.5">
                        <i
                          className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[editValues.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} ${SHIFT_TYPE_ICON_COLORS[editValues.shift_rate_type as keyof typeof SHIFT_TYPE_ICON_COLORS] || ''}`}
                        />
                        <span className="tw:text-[10px] tw:text-zinc-500">
                          {shiftTypeLabels[editValues.shift_rate_type] ||
                            editValues.shift_rate_type}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="tw:flex tw:flex-col tw:items-center tw:leading-tight">
                      <span className="tw:[text-decoration:none]">
                        <i
                          className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} ${SHIFT_TYPE_ICON_COLORS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICON_COLORS] || ''}`}
                        />
                      </span>
                      <span className="tw:text-[10px] tw:text-zinc-500">
                        {shiftTypeLabels[shift.shift_rate_type] || shift.shift_rate_type}
                      </span>
                    </div>
                  )}
                </TableCell>
                {/* Bonus */}
                {showBonus && (
                  <TableCell className="tw:text-right">
                    {editValues &&
                    editValues.bonus_amount !== (shift.bonus_amount || 0) &&
                    !isCanceled ? (
                      <>
                        <div className="tw:text-xs tw:text-zinc-400 tw:line-through">
                          {formatMoney(shift.bonus_amount)}
                        </div>
                        <div>{formatMoney(editValues.bonus_amount)}</div>
                      </>
                    ) : (
                      formatMoney(shift.bonus_amount)
                    )}
                  </TableCell>
                )}
                {/* Holiday */}
                {showHoliday && (
                  <TableCell className="tw:text-right">
                    {editValues &&
                    editValues.holiday_amount !== (shift.holiday_amount || 0) &&
                    !isCanceled ? (
                      <>
                        <div className="tw:text-xs tw:text-zinc-400 tw:line-through">
                          {formatMoney(shift.holiday_amount)}
                        </div>
                        <div>{formatMoney(editValues.holiday_amount)}</div>
                      </>
                    ) : (
                      formatMoney(shift.holiday_amount)
                    )}
                  </TableCell>
                )}
                {/* Status — wrap in span to break text-decoration inheritance from row */}
                <TableCell className="tw:text-center">
                  <span className="tw:inline-block tw:[text-decoration:none]">
                    {isCanceled ? (
                      <span className="tw:text-xs tw:text-stoplight-danger tw:font-medium">
                        CANCEL
                      </span>
                    ) : (
                      <StatusPill
                        scope={WorkflowScopes.AssignmentShift}
                        status={shift.current_status}
                      />
                    )}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}

          {/* Staged additions */}
          {pendingChanges.additions.map((addition, index) => (
            <TableRow
              key={`addition-${index}`}
              className="tw:!border-l-4 tw:!border-l-stoplight-success"
            >
              <TableCell>
                {canEdit && (
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <button
                      type="button"
                      className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                      onClick={() => {
                        setEditingShift(null);
                        setEditingAdditionIndex(index);
                        setEditModalMode('add');
                        setEditModalOpen(true);
                      }}
                      title="Edit new shift"
                    >
                      <i className="fas fa-pencil tw:text-sm" />
                    </button>
                    <button
                      type="button"
                      className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                      onClick={() => removeAddition(index)}
                      title="Remove"
                    >
                      <i className="fas fa-xmark tw:text-sm" />
                    </button>
                  </div>
                )}
              </TableCell>
              <TableCell className="tw:whitespace-nowrap">
                <div>{formatDateTimeRange(addition)}</div>
                {showFacility && (
                  <div className="tw:text-[12px] tw:leading-tight tw:text-zinc-500">
                    {addition.facility_name}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {calcDuration(
                  addition.start_on,
                  addition.start_time,
                  addition.end_on,
                  addition.end_time
                )}
              </TableCell>
              <TableCell>
                <div className="tw:flex tw:flex-col tw:items-center tw:leading-tight">
                  <i
                    className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[addition.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} ${SHIFT_TYPE_ICON_COLORS[addition.shift_rate_type as keyof typeof SHIFT_TYPE_ICON_COLORS] || ''}`}
                  />
                  <span className="tw:text-[10px] tw:text-zinc-500">
                    {shiftTypeLabels[addition.shift_rate_type] || addition.shift_rate_type}
                  </span>
                </div>
              </TableCell>
              {showBonus && (
                <TableCell className="tw:text-right">
                  {formatMoney(addition.bonus_amount)}
                </TableCell>
              )}
              {showHoliday && (
                <TableCell className="tw:text-right">
                  {formatMoney(addition.holiday_amount)}
                </TableCell>
              )}
              <TableCell className="tw:text-center">
                <span className="tw:text-xs tw:text-stoplight-success tw:font-medium">NEW</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Submit bar */}
      {hasPendingChanges && (
        <div className="tw:mt-4 tw:p-3 tw:bg-rose-50 tw:border tw:border-stoplight-danger tw:rounded-lg">
          <div className="tw:flex tw:items-center tw:justify-between tw:mb-2">
            <span className="tw:text-sm tw:text-zinc-600">
              Pending Changes:{' '}
              {summaryParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="tw:text-zinc-400">, </span>}
                  {part}
                </React.Fragment>
              ))}
            </span>
          </div>
          <textarea
            className="tw:bg-zinc-50 tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
            rows={2}
            placeholder="Add a comment about these changes..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="tw:flex tw:justify-end tw:gap-2 tw:mt-2">
            <Button plain onClick={discardAll}>
              Discard All
            </Button>
            <Button color="brandgreen0" onClick={handleSubmit} disabled={submitMutation.isLoading}>
              {submitMutation.isLoading ? 'Submitting...' : 'Submit Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Book Again */}
      {assignment.provider?.id &&
        (() => {
          const tokenParam = assignment.provider.availability_token
            ? `&token=${assignment.provider.availability_token}`
            : '';
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          const bookAgainUrl = `/app/organization/assignments/create?provider=${assignment.provider.id}&facility=${assignment.facility?.id || ''}${tokenParam}&returnUrl=${returnUrl}`;
          return (
            <div className="tw:flex tw:justify-end tw:mt-3">
              <Link to={bookAgainUrl} className="tw:no-underline">
                <Button small color="brand-green-outline">
                  <i className="fas fa-rotate-right tw:mr-1" />
                  Book Again
                </Button>
              </Link>
            </div>
          );
        })()}

      {/* Edit/Add Shift Modal */}
      {canEdit && (
        <ShiftEditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingShift(null);
            setEditingAdditionIndex(null);
          }}
          onApply={(values) => {
            if (editModalMode === 'edit' && editingShift) {
              stageEdit(editingShift.id, values as ShiftEditValues);
            } else if (editModalMode === 'add' && editingAdditionIndex !== null) {
              updateAddition(editingAdditionIndex, values as NewShiftValues);
            } else if (editModalMode === 'add') {
              stageAddition(values as NewShiftValues);
            }
            setEditingAdditionIndex(null);
          }}
          mode={editModalMode}
          shift={editingShift || undefined}
          pendingEdit={editingShift ? pendingChanges.edits[editingShift.id] : undefined}
          existingAddition={
            editingAdditionIndex !== null
              ? pendingChanges.additions[editingAdditionIndex]
              : undefined
          }
          assignmentFacility={{
            id: assignment.facility?.id || '',
            name: assignment.facility?.name || '',
          }}
          connectedFacility={connectedFacility}
          facilityOptions={facilityOptions}
          providerToken={providerToken}
          firstShiftDate={assignment.start_on || undefined}
        />
      )}

      <ShiftHistoryModal
        open={!!historyShift}
        onClose={() => setHistoryShift(null)}
        shift={historyShift || undefined}
      />
    </div>
  );
};

// Re-export for use by parent components
export { type ShiftEditValues as ShiftEditValuesType, type NewShiftValues as NewShiftValuesType };
